/**
 * Date utility functions for consistent date formatting across the application
 */

/**
 * Format a date string to a readable format
 * @param dateString - ISO date string
 * @returns Formatted date string
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A'

  try {
    // Handle the specific format from API: "2025-05-20T15:38:53"
    const date = new Date(dateString)

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString)
      return 'Invalid Date'
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch (error) {
    console.error('Error formatting date:', error, 'Input:', dateString)
    return 'Invalid Date'
  }
}

/**
 * Format a date string to include time
 * @param dateString - ISO date string
 * @returns Formatted date and time string
 */
export const formatDateTime = (dateString: string): string => {
  if (!dateString) return 'N/A'

  try {
    const date = new Date(dateString)

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date'
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (error) {
    console.error('Error formatting date time:', error)
    return 'Invalid Date'
  }
}

/**
 * Format a date string to include time with seconds (for logs)
 * @param dateString - ISO date string
 * @returns Formatted timestamp string with seconds
 */
export const formatTimestamp = (dateString: string): string => {
  if (!dateString) return 'N/A'

  try {
    const date = new Date(dateString)

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date'
    }

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  } catch (error) {
    console.error('Error formatting timestamp:', error)
    return 'Invalid Date'
  }
}

/**
 * Format a date string to a relative time (e.g., "2 days ago")
 * @param dateString - ISO date string
 * @returns Relative time string
 */
export const formatRelativeTime = (dateString: string, now: Date = new Date()): string => {
  if (!dateString) return 'N/A'

  try {
    const date = new Date(dateString)

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date'
    }

    const diffInMs = now.getTime() - date.getTime()
    const diffInSeconds = Math.floor(diffInMs / 1000)
    const diffInMinutes = Math.floor(diffInSeconds / 60)
    const diffInHours = Math.floor(diffInMinutes / 60)
    const diffInDays = Math.floor(diffInHours / 24)

    if (diffInSeconds < 60) {
      return 'Just now'
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`
    } else if (diffInDays < 30) {
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`
    } else {
      // For older dates, show the actual date
      return formatDate(dateString)
    }
  } catch (error) {
    console.error('Error formatting relative time:', error)
    return 'Invalid Date'
  }
}

/**
 * Check if a date is today
 * @param dateString - ISO date string
 * @returns True if the date is today
 */
export const isToday = (dateString: string): boolean => {
  if (!dateString) return false

  try {
    const date = new Date(dateString)
    const today = new Date()

    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    )
  } catch (error) {
    return false
  }
}

/**
 * Get the start of today as an ISO string
 * @returns ISO string for start of today
 */
export const getTodayStart = (): string => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return today.toISOString()
}

/**
 * Get the end of today as an ISO string
 * @returns ISO string for end of today
 */
export const getTodayEnd = (): string => {
  const today = new Date()
  today.setHours(23, 59, 59, 999)
  return today.toISOString()
}

/**
 * Format a date string with weekday for detailed display
 * @param dateString - ISO date string
 * @returns Formatted date with weekday (e.g., "Wed, Jan 29, 2025, 3:45 PM")
 */
export const formatDateWithWeekday = (dateString: string): string => {
  if (!dateString) return 'N/A'

  try {
    const date = new Date(dateString)

    if (isNaN(date.getTime())) {
      return 'Invalid Date'
    }

    return date.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  } catch (error) {
    console.error('Error formatting date with weekday:', error)
    return 'Invalid Date'
  }
}

/** A time of day such as "3:05 PM", or '' for a missing or unreadable date. */
export const formatClockTime = (dateString?: string | null): string => {
  const date = new Date(dateString ?? '')
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

/**
 * Format a date to compact relative time (e.g., "2d ago", "3h ago", "Sep 13", "Jan 2024")
 * Useful for space-constrained UI elements
 * @param dateString - ISO date string
 * @returns Compact relative time string
 */
export const formatCompactRelativeTime = (dateString: string): string => {
  if (!dateString) return 'N/A'

  try {
    const date = new Date(dateString)
    const now = new Date()

    if (isNaN(date.getTime())) {
      return 'Invalid Date'
    }

    const diffInMs = now.getTime() - date.getTime()
    const diffInSeconds = Math.floor(diffInMs / 1000)
    const diffInMinutes = Math.floor(diffInSeconds / 60)
    const diffInHours = Math.floor(diffInMinutes / 60)
    const diffInDays = Math.floor(diffInHours / 24)
    const diffInWeeks = Math.floor(diffInDays / 7)

    if (diffInSeconds < 60) {
      return 'now'
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`
    } else if (diffInDays < 30) {
      return `${diffInWeeks}w ago`
    } else if (diffInDays < 365) {
      // Within the last year, month + day is unambiguous
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })
    } else {
      // Over a year old - drop the day and show the year so "Sep 13" isn't read as this year
      return date.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      })
    }
  } catch (error) {
    console.error('Error formatting compact relative time:', error)
    return 'Invalid Date'
  }
}

/**
 * Format bytes to a human-readable file size
 * @param bytes - File size in bytes
 * @returns Formatted file size string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * Format bytes to a compact file size (e.g., "1.5 KB")
 * Alias for formatFileSize for consistency with inline formatBytes patterns
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export const formatBytes = (bytes?: number): string => {
  if (!bytes) return '-'
  return formatFileSize(bytes)
}

/**
 * Format a number as currency (USD)
 * @param amount - The amount to format
 * @param currency - Currency code (default: 'USD')
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export const formatCurrency = (amount: number, currency = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(amount)
}
